import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Add, Close, Delete, Refresh, Remove, Send } from '@mui/icons-material'
import { apiService } from '../../../services/api'
import type { PalworldItem } from '../../../types/palworldItems'

export type PalworldGrantLine = {
  id: string
  item: PalworldItem
  quantity: number
}

type PalworldAccountNameOption = {
  id: string
  name: string
}

const clampQty = (q: number) => Math.max(1, Math.trunc(Number(q) || 1))

const PalworldGrantDrawer: React.FC<{
  open: boolean
  onClose: () => void
  lines: PalworldGrantLine[]
  setLineQuantity: (id: string, quantity: number) => void
  removeLine: (id: string) => void
  clearCart: () => void
}> = ({ open, onClose, lines, setLineQuantity, removeLine, clearCart }) => {
  const [users, setUsers] = useState<PalworldAccountNameOption[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)

  const [selectedUser, setSelectedUser] = useState<PalworldAccountNameOption | null>(null)
  const [descricao, setDescricao] = useState('')

  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [successOpen, setSuccessOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>('')

  const totalItems = useMemo(() => lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0), [lines])

  const userLabel = useMemo(() => {
    const counts = new Map<string, number>()
    for (const u of users) {
      const key = (u.name || '').trim().toLowerCase()
      counts.set(key, (counts.get(key) || 0) + 1)
    }
    return (u: PalworldAccountNameOption) => {
      const name = u.name || '—'
      const key = name.trim().toLowerCase()
      const dup = (counts.get(key) || 0) > 1
      if (!dup) return name
      const tail = (u.id || '').slice(-6)
      return `${name} (${tail})`
    }
  }, [users])

  const loadUsers = useCallback(async () => {
    setUsersLoading(true)
    setUsersError(null)
    try {
      const data = await apiService.listarUsuariosPalworldAccountNames()
      setUsers(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setUsers([])
      setUsersError(e?.message || 'Erro ao carregar usuários (account names).')
    } finally {
      setUsersLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    if (users.length > 0) return
    void loadUsers()
  }, [loadUsers, open, users.length])

  const canSend = useMemo(() => {
    if (sending) return false
    if (lines.length === 0) return false
    if (!selectedUser?.id) return false
    if (!descricao.trim()) return false
    return true
  }, [descricao, lines.length, selectedUser?.id, sending])

  const doSend = async () => {
    if (!canSend) return
    if (!selectedUser?.id) return
    setSendError(null)
    setSending(true)
    try {
      const payload = {
        userId: selectedUser.id,
        reason: descricao.trim(),
        items: lines.map((l) => ({ itemId: l.item.id, quantity: clampQty(l.quantity) }))
      }

      await apiService.enviarPalworldItensGratisAdmin(payload)

      const msg =
        `Itens enviados para "${selectedUser?.name}". ` +
        'O usuário precisa estar online no jogo para receber imediatamente. ' +
        'Se estiver offline, quando ficar online basta entrar na área logada > "Itens pendentes de Entrega" > "Entregar itens pendentes".'

      clearCart()
      setDescricao('')
      setSelectedUser(null)
      setSuccessMessage(msg)
      setSuccessOpen(true)
      onClose()
    } catch (e: any) {
      setSendError(e?.message || 'Erro ao enviar itens.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => (sending ? null : onClose())}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 540 },
            backgroundColor: 'rgba(12, 13, 24, 0.98)',
            borderLeft: '1px solid rgba(255,152,0,0.22)'
          }
        }}
        transitionDuration={250}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Send sx={{ color: '#ff9800' }} />
              <Typography variant="h6" fontWeight={900}>
                Enviar itens (Admin)
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'text.secondary' }} disabled={sending}>
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            <Stack spacing={2}>
              <Alert severity="info">
                O usuário precisa estar online para receber imediatamente. Se estiver offline, ele pode entregar depois em
                “Área logada” &gt; “Itens pendentes de Entrega” &gt; “Entregar itens pendentes”.
              </Alert>

              {usersError && <Alert severity="warning">{usersError}</Alert>}

              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Autocomplete
                  fullWidth
                  options={users}
                  value={selectedUser}
                  loading={usersLoading}
                  onChange={(_e, v) => setSelectedUser(v)}
                  getOptionLabel={(opt) => userLabel(opt)}
                  componentsProps={{
                    paper: {
                      sx: {
                        bgcolor: '#0c0e1c',
                        backgroundImage: 'none',
                        border: '1px solid rgba(255,255,255,0.14)'
                      }
                    },
                    popper: {
                      sx: {
                        zIndex: (theme) => theme.zIndex.modal + 2
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'rgba(0,0,0,0.22)'
                    },
                    '& .MuiAutocomplete-listbox': {
                      p: 0.5
                    },
                    '& .MuiAutocomplete-option': {
                      borderRadius: 1.5
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Usuário (nome da conta)"
                      required
                      placeholder={usersLoading ? 'Carregando…' : 'Selecione o usuário'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => void loadUsers()}
                  disabled={usersLoading || sending}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                  aria-label="Recarregar usuários"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Stack>

              <TextField
                label="Descrição / motivo do envio"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                required
                fullWidth
                multiline
                minRows={3}
                placeholder="Ex.: Compensação por indisponibilidade do servidor, prêmio de evento, suporte, etc."
              />

              <Divider sx={{ opacity: 0.25 }} />

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Itens no envio
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total: {totalItems}
                </Typography>
              </Box>

              {lines.length === 0 ? (
                <Box sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Nenhum item selecionado</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Volte na lista e clique em “+1” para adicionar itens a este envio.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {lines.map((l) => (
                    <Box
                      key={l.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.10)',
                        bgcolor: 'rgba(255,255,255,0.03)'
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={l.item.imageUrl}
                        alt={l.item.name ?? l.item.assetName ?? 'item'}
                        sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.08)' }}
                        imgProps={{ loading: 'lazy' }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                          {l.item.name ?? '—'}
                        </Typography>
                        {!!l.item.assetName && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {l.item.assetName}
                          </Typography>
                        )}
                      </Box>

                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => setLineQuantity(l.id, clampQty(l.quantity - 1))}
                          sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                          aria-label="Diminuir quantidade"
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          value={clampQty(l.quantity)}
                          onChange={(e) => setLineQuantity(l.id, clampQty(Number(e.target.value)))}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, style: { textAlign: 'center', width: 72 } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setLineQuantity(l.id, clampQty(l.quantity + 1))}
                          sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                          aria-label="Aumentar quantidade"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Stack>

                      <IconButton
                        onClick={() => removeLine(l.id)}
                        sx={{ color: 'text.secondary' }}
                        aria-label="Remover item"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {sendError && <Alert severity="error">{sendError}</Alert>}
            </Stack>
          </Box>

          <Box sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(0,0,0,0.18)' }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={clearCart}
                  disabled={sending || lines.length === 0}
                  sx={{ color: 'text.secondary', flex: 1 }}
                >
                  Limpar carrinho
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => void doSend()}
                  disabled={!canSend}
                  sx={{ flex: 2, fontWeight: 900, py: 1.2, background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)', color: '#1a1206' }}
                >
                  {sending ? 'Enviando…' : 'Enviar'}
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Este fluxo não registra `warzone_coins`. Ele apenas cria itens pendentes de entrega para o usuário.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.86)' } }}
        PaperProps={{
          sx: {
            bgcolor: '#0c0e1c',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Envio realizado</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: 'text.secondary' }}>{successMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setSuccessOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PalworldGrantDrawer
